import React, { createContext, useState, useEffect, useContext, ReactNode } from 'react';
import { supabase } from '../services/supabase';
import { useAuth } from './AuthContext';
import { apiCache } from '../services/apiCache';

// Types for roles
export interface AdminRole {
  id: string;
  user_id: string;
  email: string;
  role: 'SUPERADMIN' | 'ADMIN';
  club_id: string | null;
  created_at: string;
  updated_at: string;
}

interface AdminRoleContextType {
  userRole: AdminRole | null;
  isSuperAdmin: boolean;
  loading: boolean;
  refetchRole: () => Promise<void>;
}

const AdminRoleContext = createContext<AdminRoleContextType>({
  userRole: null,
  isSuperAdmin: false,
  loading: true,
  refetchRole: async () => {}
});

// Cache keys
const ROLE_CACHE_KEY_PREFIX = 'user_role_';
const ROLE_CACHE_TTL = 5 * 60 * 1000; // 5 minutes

export const AdminRoleProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [userRole, setUserRole] = useState<AdminRole | null>(null);
  const [isSuperAdmin, setIsSuperAdmin] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);

  const fetchUserRole = async () => {
    if (!user) {
      setUserRole(null);
      setIsSuperAdmin(false);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      
      // Cache key based on user ID
      const cacheKey = `${ROLE_CACHE_KEY_PREFIX}${user.id}`;
      
      // Try to get from cache or fetch from API
      const roleData = await apiCache.getOrFetch<AdminRole | null>(
        cacheKey,
        async () => {
          // Fetch user role from user_roles table
          const { data, error } = await supabase
            .from('user_roles')
            .select('*')
            .eq('user_id', user.id)
            .single();
          
          if (error || !data) {
            console.error('Error fetching user role:', error);
            return null;
          }
          
          // Return properly typed role data
          return {
            id: data.id,
            user_id: data.user_id,
            email: data.email,
            role: data.role as 'SUPERADMIN' | 'ADMIN',
            club_id: data.club_id,
            created_at: data.created_at,
            updated_at: data.updated_at
          } as AdminRole;
        },
        ROLE_CACHE_TTL
      );
      
      // Update state with fetched data
      setUserRole(roleData);
      setIsSuperAdmin(roleData?.role === 'SUPERADMIN' || false);
    } catch (error) {
      console.error('Error in fetchUserRole:', error);
      setUserRole(null);
      setIsSuperAdmin(false);
    } finally {
      setLoading(false);
    }
  };

  // Fetch role whenever the user changes
  useEffect(() => {
    // Clear cache when user changes
    if (user) {
      const oldCacheKey = `${ROLE_CACHE_KEY_PREFIX}${user.id}`;
      apiCache.remove(oldCacheKey);
    }
    
    fetchUserRole();
  }, [user?.id]); // Only dependency is user ID, not the entire user object

  const value = {
    userRole,
    isSuperAdmin,
    loading,
    refetchRole: fetchUserRole
  };

  return <AdminRoleContext.Provider value={value}>{children}</AdminRoleContext.Provider>;
};

export const useAdminRole = (): AdminRoleContextType => {
  const context = useContext(AdminRoleContext);
  return context;
};